// Drawola Studio: whether the root page shows the welcome or the Studio, and
// whether the library holds the visitor's own work.
//
// Fork-only. The page itself follows the welcome acknowledgment only (see
// `welcomeAcknowledgment.ts`). The content check decides what the welcome CTA
// does and the has-draws hint. A fresh browser already holds one empty
// "Untitled" doc (`storage/migrate.ts`), so a doc count can never tell a new
// visitor apart; that check is read from what the docs contain.

import type { DocMeta } from "../storage/types";

export type StudioMode = "welcome" | "studio";

/**
 * `sizeBytes` of the empty starter doc made by `createDoc`: `[]` elements plus
 * `{}` appState. Pinned by `studioMode.test.ts` against the real `createDoc`.
 */
export const EMPTY_DOC_BYTES = 4;

/**
 * Whether any doc holds the visitor's own work.
 *
 * Size alone is enough for local edits; the other signals cover docs whose
 * scene is still empty locally but that the visitor already synced or shared.
 * An opened starter doc also counts: the editor autosave stores the full view
 * state, so it grows past the empty size even with no elements.
 */
export const hasUserDrawings = (docs: readonly DocMeta[]): boolean =>
  docs.some(
    (doc) =>
      doc.sizeBytes > EMPTY_DOC_BYTES ||
      !!doc.thumbnail ||
      !!doc.cloud ||
      (doc.shares?.length ?? 0) > 0,
  );

/**
 * Classifies the library: "studio" for own work or a live Drive session,
 * "welcome" for an empty one. It no longer picks the page; it decides whether
 * the welcome CTA creates a draw, and the has-draws hint.
 */
export const resolveStudioMode = ({
  docs,
  driveConnected,
}: {
  docs: readonly DocMeta[];
  driveConnected: boolean;
}): StudioMode =>
  driveConnected || hasUserDrawings(docs) ? "studio" : "welcome";

/** Picks the page: the root shows the welcome until the visitor acknowledges it. */
export const resolvePageMode = ({
  isRoot,
  acknowledged,
}: {
  isRoot: boolean;
  acknowledged: boolean;
}): StudioMode => (isRoot && !acknowledged ? "welcome" : "studio");
